import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import LessonCard from './components/LessonCard';
import StudyGuideCard from './components/StudyGuideCard';
import { numberSystemLessons } from './data/numberSystemLessons';
import { algebraLessons } from './data/algebraLessons';
import { functionsLessons } from './data/functionsLessons';
import { statisticsLessons } from './data/statisticsLessons';
import { studyGuidesData } from './data/studyGuidesData';
import { useProgress } from '../../../hooks/useProgress';
import SEOHead from '../../../components/SEOHead';

export default function Grade9Page() {
  const [activeTab, setActiveTab] = useState<'lessons' | 'worksheets' | 'guides'>('lessons');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUnit, setSelectedUnit] = useState<'all' | '1' | '2' | '3' | '4'>('all');
  const [selectedStandard, setSelectedStandard] = useState<'all' | 'CCSS' | 'NY' | 'OH' | 'NCTM'>('all');
  const { getUnitProgress } = useProgress();

  const allLessons = [
    ...numberSystemLessons,
    ...algebraLessons,
    ...functionsLessons,
    ...statisticsLessons
  ];

  const units = [
    { id: '1', name: 'Unit 1: Number Systems', lessons: numberSystemLessons, color: 'from-blue-500 to-cyan-500' },
    { id: '2', name: 'Unit 2: Equations & Inequalities', lessons: algebraLessons, color: 'from-purple-500 to-pink-500' },
    { id: '3', name: 'Unit 3: Functions & Graphing', lessons: functionsLessons, color: 'from-emerald-500 to-teal-500' },
    { id: '4', name: 'Unit 4: Statistics & Data', lessons: statisticsLessons, color: 'from-orange-500 to-red-500' }
  ];

  // Filter lessons based on search, unit, and standard
  const filteredLessons = useMemo(() => {
    return allLessons.filter(lesson => {
      // Unit filter
      if (selectedUnit !== 'all') {
        const lessonUnit = lesson.id.split('.')[0];
        if (lessonUnit !== selectedUnit) return false;
      }

      // Standard filter
      if (selectedStandard !== 'all') {
        const hasStandard = lesson.standards.some(std => std.startsWith(selectedStandard));
        if (!hasStandard) return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = lesson.title.toLowerCase().includes(query);
        const matchesVocab = lesson.vocabulary.some(v => v.term.toLowerCase().includes(query));
        const matchesStandards = lesson.standards.some(s => s.toLowerCase().includes(query));
        return matchesTitle || matchesVocab || matchesStandards;
      }

      return true;
    });
  }, [searchQuery, selectedUnit, selectedStandard]);

  // Highlight matching text
  const highlightText = (text: string) => {
    if (!searchQuery.trim()) return text;
    const query = searchQuery.toLowerCase();
    const index = text.toLowerCase().indexOf(query);
    if (index === -1) return text;
    
    return (
      <>
        {text.slice(0, index)}
        <span className="bg-yellow-200 px-1 rounded">{text.slice(index, index + searchQuery.length)}</span>
        {text.slice(index + searchQuery.length)}
      </>
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedUnit('all');
    setSelectedStandard('all');
  };

  const hasActiveFilters = searchQuery.trim() || selectedUnit !== 'all' || selectedStandard !== 'all';

  // Calculate overall progress
  const totalLessons = allLessons.length;
  const completedLessons = allLessons.filter(lesson => {
    const progress = getUnitProgress(`grade-9-${lesson.id}`);
    return progress.completed > 0;
  }).length;
  const overallProgress = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: 'Grade 9 Algebra I | New York Mathematics | Augustine Anyimadu-Ahenkae',
    description:
      'Complete Grade 9 Algebra I curriculum covering Number Systems, Equations & Inequalities, Functions & Graphing, and Statistics. Aligned with CCSS, NY, Ohio, and NCTM standards.',
    url: `${siteUrl}/grades/grade-9`,
    provider: { '@type': 'Person', name: 'Augustine Anyimadu-Ahenkae', url: siteUrl },
    educationalLevel: 'Grade 9',
    inLanguage: 'en',
    hasCourseInstance: [
      { '@type': 'CourseInstance', name: 'Unit 1: Number Systems', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Unit 2: Equations & Inequalities', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Unit 3: Functions & Graphing', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Unit 4: Statistics & Data', courseMode: 'online' },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <SEOHead
        title="Grade 9 Algebra I New York | Common Core Math Lessons | Augustine Anyimadu-Ahenkae"
        description="Master Grade 9 Algebra I with interactive lessons on Number Systems, Equations & Inequalities, Functions, and Statistics. Aligned with CCSS, NY Next Gen, and AP standards. Progress tracking included."
        canonical="/grades/grade-9"
        keywords="Grade 9 Algebra, Algebra I lessons, New York grade 9 math, Common Core Algebra, linear equations, functions graphing, 9th grade mathematics"
        jsonLd={jsonLd}
      />
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/grades/hub" 
                className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl text-slate-700"></i>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Grade 9 Algebra I</h1>
                <p className="text-sm text-slate-600 mt-1">Complete curriculum aligned with Common Core, NY, OH, and NCTM standards</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-sm font-medium text-slate-700">{completedLessons} / {totalLessons} Lessons</div>
                <div className="text-xs text-slate-500">{overallProgress}% Complete</div>
              </div>
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">{overallProgress}%</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-6 border-b border-slate-200">
            <button
              onClick={() => setActiveTab('lessons')}
              className={`px-6 py-3 font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'lessons'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <i className="ri-book-open-line mr-2"></i>
              Interactive Lessons
            </button>
            <button
              onClick={() => setActiveTab('worksheets')}
              className={`px-6 py-3 font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'worksheets'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <i className="ri-file-text-line mr-2"></i>
              Practice Worksheets
            </button>
            <button
              onClick={() => setActiveTab('guides')}
              className={`px-6 py-3 font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'guides'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <i className="ri-file-list-3-line mr-2"></i>
              Study Guides
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'lessons' && (
          <>
            {/* Search & Filter Bar */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6 sticky top-[180px] z-30">
              <div className="flex flex-col gap-4">
                {/* Search Input */}
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg"></i>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search lessons by topic, keyword, or standard..."
                    className="w-full pl-12 pr-10 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-slate-500"></i>
                    </button>
                  )}
                </div>

                {/* Filter Buttons */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* Unit Filters */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-600">Unit:</span>
                    {['all', '1', '2', '3', '4'].map((unit) => (
                      <button
                        key={unit}
                        onClick={() => setSelectedUnit(unit as any)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                          selectedUnit === unit
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {unit === 'all' ? 'All Units' : `Unit ${unit}`}
                      </button>
                    ))}
                  </div>

                  {/* Standard Filter */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-600">Standard:</span>
                    <select
                      value={selectedStandard}
                      onChange={(e) => setSelectedStandard(e.target.value as any)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border-none focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    >
                      <option value="all">All Standards</option>
                      <option value="CCSS">Common Core (CCSS)</option>
                      <option value="NY">New York (NY)</option>
                      <option value="OH">Ohio (OH)</option>
                      <option value="NCTM">NCTM</option>
                    </select>
                  </div>

                  {/* Results Count */}
                  <div className="ml-auto flex items-center gap-3">
                    <span className="text-sm text-slate-600">
                      Showing <span className="font-semibold text-slate-900">{filteredLessons.length}</span> of <span className="font-semibold text-slate-900">{totalLessons}</span> lessons
                    </span>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-refresh-line mr-1"></i>
                        Clear Filters
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Lessons Display */}
            {filteredLessons.length === 0 ? (
              // Empty State
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-4xl text-slate-400"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">No lessons found</h3>
                <p className="text-slate-600 mb-6">Try adjusting your search or filters to find what you're looking for.</p>
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line mr-2"></i>
                  Clear All Filters
                </button>
              </div>
            ) : hasActiveFilters ? (
              // Flat Filtered List
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredLessons.map((lesson) => (
                  <LessonCard 
                    key={lesson.id} 
                    lesson={{
                      ...lesson,
                      title: highlightText(lesson.title) as any
                    }} 
                  />
                ))}
              </div>
            ) : (
              // Grouped by Units
              <div className="space-y-12">
                {units.map((unit) => {
                  const unitProgress = getUnitProgress(`grade-9-unit-${unit.id}`);
                  const unitLessons = unit.lessons;
                  const completedInUnit = unitLessons.filter(lesson => {
                    const progress = getUnitProgress(`grade-9-${lesson.id}`);
                    return progress.completed > 0;
                  }).length;
                  const unitPercentage = unitLessons.length > 0 ? Math.round((completedInUnit / unitLessons.length) * 100) : 0;

                  return (
                    <div key={unit.id}>
                      {/* Unit Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${unit.color} flex items-center justify-center shadow-lg`}>
                            <span className="text-white font-bold text-lg">{unit.id}</span>
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold text-slate-900">{unit.name}</h2>
                            <p className="text-sm text-slate-600 mt-1">{unitLessons.length} lessons</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-sm font-medium text-slate-700">{completedInUnit} / {unitLessons.length} Complete</div>
                            <div className="text-xs text-slate-500">{unitPercentage}% Progress</div>
                          </div>
                          <div className="w-24 h-3 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full bg-gradient-to-r ${unit.color} transition-all duration-500`}
                              style={{ width: `${unitPercentage}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* Lessons Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {unitLessons.map((lesson) => (
                          <LessonCard key={lesson.id} lesson={lesson} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'worksheets' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto mb-6">
              <i className="ri-file-text-line text-4xl text-white"></i>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">100+ Printable Worksheets</h2>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
              Get access to comprehensive practice worksheets for every lesson, including answer keys and step-by-step solutions. Perfect for homework, test prep, and extra practice.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link
                to="/"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-vip-crown-line mr-2"></i>
                Upgrade to Access Worksheets
              </Link>
            </div>
          </div>
        )}

        {activeTab === 'guides' && (
          <div className="space-y-8">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-8 text-white shadow-lg">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-3xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Printable Study Guides</h2>
                  <p className="text-blue-100 mt-1">Quick reference sheets for all key formulas, vocabulary, and concepts</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {studyGuidesData.map((guide, index) => (
                <StudyGuideCard key={index} guide={guide} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}